import { Box, Grid, MenuItem, Paper, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import LoadingDialog from "../../../../shared/components/LoadingModalDialog/LoadingModalDialog";
import { VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { globalColorTytle, RangeStates, typeProperty } from "../../../../shared/utils";

interface ISectionProps {
    data?: any;
    formRef: any;
}

export const CapitalizationPropertySection: React.FC<ISectionProps> = ({ data, formRef }) => {
    const [isLoadingCEP, setIsLoadingCEP] = useState(false);
    const [lastPropertyZipcode, setLastPropertyZipcode] = useState("");
    const toastHandler = useToast();
    const addressNumberRef = useRef<HTMLInputElement | null>(null);
    const addressZipcodeRef = useRef<HTMLInputElement | null>(null);

    const showToast = (title: string, content: string) => {
        toastHandler.present({
            type: "error",
            position: "top-right",
            messageOrContent: <ToastContent title={title} content={content} />,
        });
    };

    const checkCEP = async (e: React.FocusEvent<HTMLInputElement>) => {
        const cep = e.target.value.replace(/\D/g, "");

        if (cep === lastPropertyZipcode) return;

        setLastPropertyZipcode(cep);
        setIsLoadingCEP(true);

        if (cep.length !== 8) {
            setIsLoadingCEP(false);
            return;
        }

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                showToast("Erro de CEP", "CEP não localizado. Verifique o valor digitado.");
                ["propertyZipcode", "propertyStreet", "propertyDistrict", "propertyCity", "propertyState"].forEach((field) =>
                    formRef.current?.setFieldValue(field, "")
                );
                addressZipcodeRef.current?.focus();
            } else {
                const fieldsToSet = {
                    "propertyZipcode": data.cep,
                    "propertyStreet": data.logradouro,
                    "propertyDistrict": data.bairro,
                    "propertyCity": data.localidade,
                    "propertyState": data.uf,
                };

                Object.entries(fieldsToSet).forEach(([key, value]) => formRef.current?.setFieldValue(key, value));
                addressNumberRef.current?.focus();
            }
        } catch {
            showToast("Erro na consulta", "Ocorreu um problema ao buscar o CEP. Tente novamente.");
            addressZipcodeRef.current?.focus();
        } finally {
            setIsLoadingCEP(false);
        }
    };

    return (
        <>
            <LoadingDialog isLoading={isLoadingCEP} loadingText={`Localizando CEP...`} />
            <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
                <Grid container direction="column" padding={2} spacing={2}>
                    <Grid item xs={11}>
                        <Typography variant="h6" color={globalColorTytle}>
                            Dados do Imóvel
                        </Typography>
                    </Grid>

                    <Grid container item direction="row" spacing={2}>
                        <Grid item xs={12} md={4}>
                            <VMaskTextField
                                fullWidth
                                label="CEP*"
                                name="propertyZipcode"
                                mask="cep"
                                onBlur={checkCEP}
                                inputRef={addressZipcodeRef}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="Logradouro*" name="propertyStreet" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="Número*" name="propertyNumber" inputRef={addressNumberRef} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="Complemento" name="propertyComplement" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="Bairro*" name="propertyDistrict" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="Cidade*" name="propertyCity" />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="UF*" name="propertyState" select>
                                {RangeStates.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </VTextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="Tipo de imóvel*" name="propertyType" select>
                                {typeProperty.map((element) => (
                                    <MenuItem key={element.description} value={element.description}>
                                        {element.description}
                                    </MenuItem>
                                ))}
                            </VTextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <VTextField fullWidth label="Tipo de locação*" name="propertyRentalType" select>
                                <MenuItem value="residential">Residencial</MenuItem>
                                <MenuItem value="commercial">Comercial</MenuItem>
                            </VTextField>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
